import { PORTFOLIO_INSTRUMENT_KIND_OBJ, type PortfolioInstrumentRef } from "@signals/shared";

// packages/front/src/_common/lib/query-keys.ts
//
// Single registry of TanStack Query keys for all frontend modules.
// Consumers import `qk` and pass keys to useQuery/useMutation/invalidateQueries.
// No inline key literals outside this file.

export const QUERY_CACHE_ACTION_TYPE_OBJ = {
  INVALIDATE: "invalidate",
  FETCH: "fetch",
  SUCCESS: "success",
} as const;

export const QUERY_FETCH_STATUS_OBJ = {
  IDLE: "idle",
} as const;

/** TanStack Query's public request state; kept beside its cache action/fetch states. */
export const QUERY_STATUS_OBJ = { ERROR: "error" } as const;

export const qk = {
  mcp: {
    tokens: ["mcp", "tokens"] as const,
  },
  portfolio: {
    all: ["portfolio"] as const,
    portfolios: ["portfolio", "portfolios"] as const,
    summary: (portfolioId: string, date?: string) =>
      ["portfolio", "summary", { portfolioId, date }] as const,
    summaryForPortfolio: (portfolioId: string) =>
      ["portfolio", "summary", { portfolioId }] as const,
    positions: (portfolioId: string, date?: string, accountId?: string, status?: string) =>
      ["portfolio", "positions", { portfolioId, date, accountId, status }] as const,
    positionsForPortfolio: (portfolioId: string) =>
      ["portfolio", "positions", { portfolioId }] as const,
    transactions: (portfolioId: string, filters?: unknown) =>
      ["portfolio", "transactions", portfolioId, filters ?? {}] as const,
    transactionsForPortfolio: (portfolioId: string) =>
      ["portfolio", "transactions", portfolioId] as const,
    positionHistory: (
      portfolioId: string,
      instrument: string | PortfolioInstrumentRef,
      valuationDate: string,
      filters?: unknown,
    ) =>
      [
        "portfolio",
        "position-history",
        portfolioId,
        { ...(typeof instrument === "string" ? { symbolId: instrument } : instrument.kind === PORTFOLIO_INSTRUMENT_KIND_OBJ.SECURITY ? { symbolId: instrument.symbolId } : { optionContractId: instrument.optionContractId }), valuationDate, filters: filters ?? {} },
      ] as const,
    positionHistoryForPortfolio: (portfolioId: string) =>
      ["portfolio", "position-history", portfolioId] as const,
    cashflows: (portfolioId: string, filters?: unknown) =>
      ["portfolio", "cashflows", portfolioId, filters ?? {}] as const,
    cashflowsForPortfolio: (portfolioId: string) =>
      ["portfolio", "cashflows", portfolioId] as const,
    incomeEvents: {
      all: ["portfolio", "income-events"] as const,
      forPortfolio: (portfolioId: string) =>
        ["portfolio", "income-events", portfolioId] as const,
      list: (portfolioId: string, filters?: unknown) =>
        ["portfolio", "income-events", portfolioId, filters ?? {}] as const,
    },
    accounts: (portfolioId: string, filters?: unknown) =>
      ["portfolio", "accounts", portfolioId, filters ?? {}] as const,
    /** Prefix of every accounts query of one portfolio (all dates and status filters). */
    accountsForPortfolio: (portfolioId: string) =>
      ["portfolio", "accounts", portfolioId] as const,
    exchangeRates: ["portfolio", "exchange-rates"] as const,
    compare: (portfolioId: string, from: string, to: string) =>
      ["portfolio", "compare", { portfolioId, from, to }] as const,
    compareForPortfolio: (portfolioId: string) =>
      ["portfolio", "compare", { portfolioId }] as const,
    comparisonOperation: (portfolioId: string, from: string, to: string, operationId: string) =>
      ["portfolio", "comparison-operation", { portfolioId, from, to, operationId }] as const,
    comparisonCompletion: (portfolioId: string, operationId: string) =>
      ["portfolio", "comparison-completion", { portfolioId, operationId }] as const,
    yearlyPerformance: {
      all: (portfolioId: string) =>
        ["portfolio", "yearly-performance", "all", portfolioId] as const,
      single: (portfolioId: string, year: number) =>
        ["portfolio", "yearly-performance", { portfolioId, year }] as const,
      singleForPortfolio: (portfolioId: string) =>
        ["portfolio", "yearly-performance", { portfolioId }] as const,
    },
    snapshots: (portfolioId: string) =>
      ["portfolio", "snapshots", portfolioId] as const,
    snapshot: (portfolioId: string, date: string) =>
      ["portfolio", "snapshot", { portfolioId, date }] as const,
    snapshotForPortfolio: (portfolioId: string) => ["portfolio", "snapshot", { portfolioId }] as const,
    snapshotRevisions: (portfolioId: string, date: string) =>
      ["portfolio", "snapshot-revisions", { portfolioId, date }] as const,
    snapshotRevisionsForPortfolio: (portfolioId: string) => ["portfolio", "snapshot-revisions", { portfolioId }] as const,
    brokerSync: {
      /** Not portfolio-scoped: one settings file per installation. */
      settings: ["portfolio", "broker-sync", "settings"] as const,
      /** Prefix of every broker-sync query of one portfolio (accounts and issues). */
      allForPortfolio: (portfolioId: string) =>
        ["portfolio", "broker-sync", portfolioId] as const,
      accounts: (portfolioId: string) =>
        ["portfolio", "broker-sync", portfolioId, "accounts"] as const,
      /** Client acknowledgement of factual run IDs; no timer or separate status request. */
      successfulRuns: (portfolioId: string) =>
        ["portfolio", "broker-sync", portfolioId, "successful-runs"] as const,
      reconciled: (portfolioId: string) =>
        ["portfolio", "broker-sync", portfolioId, "reconciled"] as const,
      issuesForPortfolio: (portfolioId: string) =>
        ["portfolio", "broker-sync", portfolioId, "issues"] as const,
      issues: (portfolioId: string, accountId?: string, status?: string) =>
        ["portfolio", "broker-sync", portfolioId, "issues", { accountId, status }] as const,
      issuesScope: (portfolioId: string, filters: {
        accountId?: string | undefined; status?: string | undefined; kind?: string | undefined; runId?: string | undefined;
        offset?: number | undefined; limit?: number | undefined; asOf?: string | undefined; from?: string | null | undefined; to?: string | null | undefined;
      }) => ["portfolio", "broker-sync", portfolioId, "issues", filters] as const,
      qualityReviewsForPortfolio: (portfolioId: string) =>
        ["portfolio", "broker-sync", portfolioId, "quality-reviews"] as const,
      qualityReviews: (portfolioId: string, filters: {
        accountId?: string | undefined; quality?: string | undefined; offset?: number | undefined; limit?: number | undefined;
        asOf?: string | undefined; from?: string | null | undefined; to?: string | null | undefined;
      }) => ["portfolio", "broker-sync", portfolioId, "quality-reviews", filters] as const,
    },
  },
  symbols: {
    all: ["symbols"] as const,
    search: (query: string) => ["symbols", "search", query] as const,
    list: (filters?: unknown) =>
      ["symbols", "list", filters ?? {}] as const,
    detail: (id: string) => ["symbols", "detail", id] as const,
    analyses: (symbol: string) => ["symbols", "analyses", symbol] as const,
    historyDigest: (symbol: string) =>
      ["symbols", "history-digest", symbol] as const,
    aiAnalyses: (symbol: string) =>
      ["symbols", "ai-analyses", symbol] as const,
  },
  pinbar: {
    all: ["pinbar"] as const,
    list: (params?: unknown) => ["pinbar", "list", params ?? {}] as const,
    detail: (id: string) => ["pinbar", "detail", id] as const,
    prompt: ["pinbar", "prompt"] as const,
  },
  datasource: {
    all: ["datasource"] as const,
    freshness: ["datasource", "freshness"] as const,
    symbolFilters: ["datasource", "symbol-filters"] as const,
  },
  scheduler: {
    all: ["scheduler"] as const,
    jobs: ["scheduler", "jobs"] as const,
    jobLogs: (jobId: string) => ["scheduler", "job-logs", jobId] as const,
    history: (filters?: unknown) =>
      ["scheduler", "history", filters ?? {}] as const,
    cron: ["scheduler", "cron"] as const,
    symbolsCron: ["scheduler", "symbols-cron"] as const,
    notifications: ["scheduler", "notifications"] as const,
  },
  backtester: {
    all: ["backtester"] as const,
    strategies: ["backtester", "strategies"] as const,
    runs: ["backtester", "runs"] as const,
    runDetail: (id: string) => ["backtester", "run-detail", id] as const,
  },
  ideas: {
    all: ["ideas"] as const,
    detail: (id: string) => ["ideas", "detail", id] as const,
    transactions: (id: string) => ["ideas", "transactions", id] as const,
  },
} as const;
